import React from 'react';
import type {
  ActionsBlock,
  AlertBlock,
  CardBlock,
  ContextBlock,
  DividerBlock,
  HeaderBlock,
  ImageBlock,
  InputBlock,
  RichTextBlock,
  SectionBlock,
} from './types';
import { Text } from './mrkdwn';
import { Button, Element, ImageEl } from './elements';
import { useTokens } from './theme';

// ─── Section ──────────────────────────────────────────────────────────────────

export function Section({ block }: { block: SectionBlock }) {
  const t = useTokens();
  return (
    <div>
      {block.fields ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
          {block.fields.map((f, i) => (
            <div key={i} style={{ fontSize: 15, color: t.text, lineHeight: '1.5' }}>
              <Text obj={f} />
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16,
            justifyContent: 'space-between',
          }}
        >
          {block.text && (
            <div style={{ fontSize: 15, color: t.text, lineHeight: '1.5', flex: 1 }}>
              <Text obj={block.text} />
            </div>
          )}
          {block.accessory && (
            <div style={{ flexShrink: 0 }}>
              <Element el={block.accessory} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────

export function Header({ block }: { block: HeaderBlock }) {
  const t = useTokens();
  return (
    <div style={{ fontSize: 18, fontWeight: 900, color: t.text, lineHeight: '1.4' }}>
      <Text obj={block.text} />
    </div>
  );
}

// ─── Divider ──────────────────────────────────────────────────────────────────

export function Divider({ block: _block }: { block: DividerBlock }) {
  const t = useTokens();
  return (
    <div style={{ padding: '4px 0' }}>
      <hr style={{ border: 'none', borderTop: `1px solid ${t.border}`, margin: 0 }} />
    </div>
  );
}

// ─── Actions ──────────────────────────────────────────────────────────────────

export function Actions({ block }: { block: ActionsBlock }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      {block.elements.map((el, i) => (
        <Element key={i} el={el} />
      ))}
    </div>
  );
}

// ─── Context ──────────────────────────────────────────────────────────────────

export function Context({ block }: { block: ContextBlock }) {
  const t = useTokens();
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
      {block.elements.map((el, i) => {
        if (el.type === 'image') {
          return (
            <img
              key={i}
              src={el.image_url}
              alt={el.alt_text}
              style={{ width: 20, height: 20, borderRadius: 3, objectFit: 'cover' }}
            />
          );
        }
        return (
          <span key={i} style={{ fontSize: 11, color: t.textMuted, lineHeight: '1.4' }}>
            <Text obj={el} />
          </span>
        );
      })}
    </div>
  );
}

// ─── Input ────────────────────────────────────────────────────────────────────

export function Input({ block }: { block: InputBlock }) {
  const t = useTokens();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: t.text,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Text obj={block.label} />
        {!block.optional && <span style={{ color: '#e01e5a', fontWeight: 700 }}>*</span>}
      </label>
      <Element el={block.element} />
      {block.hint && (
        <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>
          <Text obj={block.hint} />
        </p>
      )}
    </div>
  );
}

// ─── Image block ──────────────────────────────────────────────────────────────

export function ImageBlock({ block }: { block: ImageBlock }) {
  const t = useTokens();
  return (
    <div>
      {block.title && (
        <p style={{ fontSize: 15, fontWeight: 700, color: t.text, marginBottom: 4 }}>
          <Text obj={block.title} />
        </p>
      )}
      <img
        src={block.image_url}
        alt={block.alt_text}
        style={{ maxWidth: '100%', borderRadius: 4, display: 'block' }}
      />
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

export function Card({ block }: { block: CardBlock }) {
  const t = useTokens();
  return (
    <div
      style={{
        border: `1px solid ${t.border}`,
        borderRadius: 8,
        overflow: 'hidden',
        background: t.surface,
      }}
    >
      {block.hero_image && (
        <img
          src={block.hero_image.image_url}
          alt={block.hero_image.alt_text}
          style={{ width: '100%', display: 'block', maxHeight: 200, objectFit: 'cover' }}
        />
      )}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(block.icon || block.title || block.subtitle) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {block.icon && (
              <img
                src={block.icon.image_url}
                alt={block.icon.alt_text}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 6,
                  objectFit: 'cover',
                  flexShrink: 0,
                }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              {block.title && (
                <div style={{ fontSize: 15, fontWeight: 700, color: t.text, lineHeight: '1.4' }}>
                  <Text obj={block.title} />
                </div>
              )}
              {block.subtitle && (
                <div style={{ fontSize: 13, color: t.textMuted, lineHeight: '1.4' }}>
                  <Text obj={block.subtitle} />
                </div>
              )}
            </div>
          </div>
        )}
        {block.body && (
          <div style={{ fontSize: 15, color: t.text, lineHeight: '1.5' }}>
            <Text obj={block.body} />
          </div>
        )}
        {block.actions && block.actions.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {block.actions.map((el, i) => (
              <Button key={i} el={el} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Alert ────────────────────────────────────────────────────────────────────

type AlertStyle = { border: string; bg: string; bgDark: string; icon: string };

const ALERT_STYLES: Record<string, AlertStyle> = {
  default: { border: '#868686', bg: '#f8f8f8', bgDark: 'rgba(134,134,134,0.12)', icon: 'ℹ' },
  info: {
    border: '#1264a3',
    bg: 'rgba(18,100,163,0.07)',
    bgDark: 'rgba(18,100,163,0.15)',
    icon: 'ℹ',
  },
  success: {
    border: '#2eb67d',
    bg: 'rgba(46,182,125,0.07)',
    bgDark: 'rgba(46,182,125,0.12)',
    icon: '✓',
  },
  warning: {
    border: '#ecb22e',
    bg: 'rgba(236,178,46,0.08)',
    bgDark: 'rgba(236,178,46,0.12)',
    icon: '⚠',
  },
  error: {
    border: '#e01e5a',
    bg: 'rgba(224,30,90,0.06)',
    bgDark: 'rgba(224,30,90,0.12)',
    icon: '✕',
  },
};

export function Alert({ block }: { block: AlertBlock }) {
  const t = useTokens();
  const dark = t.bg !== '#ffffff';
  const level = block.level ?? 'default';
  const s = ALERT_STYLES[level] ?? ALERT_STYLES.default;
  return (
    <div
      style={{
        borderLeft: `4px solid ${s.border}`,
        borderRadius: 4,
        background: dark ? s.bgDark : s.bg,
        padding: '10px 12px',
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
      }}
    >
      <span style={{ fontSize: 14, color: s.border, flexShrink: 0, fontWeight: 700 }}>
        {s.icon}
      </span>
      <div style={{ fontSize: 15, color: t.text, lineHeight: '1.5' }}>
        <Text obj={block.text} />
      </div>
    </div>
  );
}

// ─── Rich Text ────────────────────────────────────────────────────────────────

export function RichText({ block: _block }: { block: RichTextBlock }) {
  const t = useTokens();
  return (
    <div style={{ fontSize: 15, color: t.textMuted, fontStyle: 'italic' }}>[rich_text block]</div>
  );
}
